import React, { useCallback, useMemo, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { getActiveCompanyId } from '../../../lib/services/company.service';
import {
  buildDeleteFormatTemplate,
  buildSetFormatTemplateGlobal,
  getFormatTemplates,
} from '../../../lib/services/format-template.service';
import { compareNatural } from '../../../lib/helpers/text.helpers';
import { hasAdminAccess } from '../../../lib/services/user.service';
import { ConfirmDialog } from '../../_core/_ui/confirm-dialog.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { EditCatalogTemplateModalContainer } from '../EditCatalogTemplateModal/edit-catalog-template-modal.container';
import { EditFormatTemplateModalContainer } from '../EditFormatTemplateModal/edit-format-template-modal.container';
import { PreviewTemplateModalGraphQL } from '../PreviewTemplateModal/preview-template-modal.graphql';
import { messages } from '../format-template.messages';
import { FormatTemplateListItem } from './format-template-list-item.component';

interface IFormatTemplateListProps {
  scope?: 'ASSOCIATION' | 'SYSTEM';
}

export const FormatTemplateList: React.FC<IFormatTemplateListProps> = ({ scope = 'ASSOCIATION' }) => {
  const isSystem = scope === 'SYSTEM';
  const companyId = getActiveCompanyId();
  const { templates, loading } = getFormatTemplates(companyId || undefined, { fetchWhenNoCompany: isSystem });
  const { isAdmin } = hasAdminAccess();
  const { deleteFormatTemplate } = buildDeleteFormatTemplate();
  const { setFormatTemplateGlobal } = buildSetFormatTemplateGlobal();

  const [error, setError] = useState<React.ReactNode>(null);
  const [notice, setNotice] = useState<React.ReactNode>(null);
  const [previewTemplate, setPreviewTemplate] = useState<IFormatTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<IFormatTemplate | null>(null);
  const [pendingDelete, setPendingDelete] = useState<IFormatTemplate | null>(null);
  const [pendingGlobal, setPendingGlobal] = useState<{ template: IFormatTemplate; value: boolean } | null>(null);

  // One query serves both lists; scope is visibility, so each surface shows only its own half,
  // name-ordered (numeric-aware, so U9 Evaluation lists before U11 Evaluation).
  const scopedTemplates = useMemo(
    () => templates.filter(t => t.scope === scope).sort((a, b) => compareNatural(a.name, b.name)),
    [templates, scope],
  );

  const handleConfirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    const { _id, name } = pendingDelete;
    setPendingDelete(null);
    setError(null);
    setNotice(null);
    deleteFormatTemplate(_id).catch(err => {
      console.error('deleteFormatTemplate failed:', err);
      setError(<IntlMsg {...messages.deleteError} values={{ name }} />);
    });
  }, [pendingDelete, deleteFormatTemplate]);

  const handleToggleGlobal = useCallback(
    (template: IFormatTemplate, value: boolean) => setPendingGlobal({ template, value }),
    [],
  );

  // The flip moves the card to the list on the other page, so the row simply vanishing here is
  // indistinguishable from a delete — it needs both a confirmation and a message naming where it went.
  const handleConfirmGlobal = useCallback(() => {
    if (!pendingGlobal) return;
    const { template, value } = pendingGlobal;
    setPendingGlobal(null);
    setError(null);
    setNotice(null);
    setFormatTemplateGlobal(template._id, value)
      .then(() => setNotice(
        <IntlMsg {...(value ? messages.globalPromoted : messages.globalDemoted)} values={{ name: template.name }} />,
      ))
      .catch(err => {
        console.error('setFormatTemplateGlobal failed:', err);
        setError(<IntlMsg {...messages.globalToggleError} values={{ name: template.name }} />);
      });
  }, [pendingGlobal, setFormatTemplateGlobal]);

  // One selection routes to one of two editors; deriving the pair once means the two modal
  // mounts can never disagree. Not memoized: cheap scalar ternaries, not fed to any hook deps.
  const catalogEditing = editingTemplate?.source === 'CATALOG' ? editingTemplate : null;
  const customEditing = editingTemplate && !catalogEditing ? editingTemplate : null;

  if (loading && !scopedTemplates.length) return <Typography><IntlMsg {...messages.loading} /></Typography>;
  if (!scopedTemplates.length) {
    return (
      <Typography color="textSecondary">
        <IntlMsg {...(isSystem ? messages.noSystemTemplates : messages.noTemplates)} />
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      {error && (
        <Grid item xs={12}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Grid>
      )}
      {notice && (
        <Grid item xs={12}>
          <Typography color="textSecondary" variant="body2">{notice}</Typography>
        </Grid>
      )}
      {scopedTemplates.map(t => (
        <Grid item xs={12} key={t._id}>
          <FormatTemplateListItem
            template={t}
            showPills={!isSystem}
            // A CATALOG family opens the admin-only settings editor (stage settings + band
            // diagrams — structure is seed-owned); a CUSTOM one opens the blueprint editor.
            canEdit={t.source !== 'CATALOG' || isAdmin}
            // A global template is offered to every association, so retiring it is not a
            // per-association decision; demote it first, then delete it from its owner's list.
            canDelete={!isSystem}
            canSetGlobal={isAdmin && t.source !== 'CATALOG'}
            onPreview={setPreviewTemplate}
            onEdit={setEditingTemplate}
            onDelete={setPendingDelete}
            onToggleGlobal={handleToggleGlobal}
          />
        </Grid>
      ))}
      <PreviewTemplateModalGraphQL
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        template={previewTemplate}
      />
      <EditFormatTemplateModalContainer
        open={!!customEditing}
        onClose={() => setEditingTemplate(null)}
        onSaved={() => setEditingTemplate(null)}
        template={customEditing}
      />
      <EditCatalogTemplateModalContainer
        open={!!catalogEditing}
        onClose={() => setEditingTemplate(null)}
        onSaved={() => setEditingTemplate(null)}
        template={catalogEditing}
      />
      <ConfirmDialog
        open={!!pendingDelete}
        title={<IntlMsg {...messages.confirmDeleteTitle} />}
        message={<IntlMsg {...messages.confirmDeleteBody} values={{ name: pendingDelete?.name }} />}
        confirmLabel={<IntlMsg {...messages.delete} />}
        cancelLabel={<IntlMsg {...messages.cancel} />}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        destructive
      />
      <ConfirmDialog
        open={!!pendingGlobal}
        title={<IntlMsg {...(pendingGlobal?.value ? messages.confirmPromoteTitle : messages.confirmDemoteTitle)} />}
        message={(
          <IntlMsg
            {...(pendingGlobal?.value ? messages.confirmPromoteBody : messages.confirmDemoteBody)}
            values={{ name: pendingGlobal?.template.name }}
          />
        )}
        confirmLabel={<IntlMsg {...(pendingGlobal?.value ? messages.confirmPromote : messages.confirmDemote)} />}
        cancelLabel={<IntlMsg {...messages.cancel} />}
        onConfirm={handleConfirmGlobal}
        onCancel={() => setPendingGlobal(null)}
        destructive={pendingGlobal ? !pendingGlobal.value : false}
      />
    </Grid>
  );
};
